import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import compose from 'lodash.flowright';

import CreateWizardLayout from '../../../../../components/CreateWizardLayout';
import FreemiumEventReviewStepUpdateModal, {
  FREEMIUM_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL,
} from './components/FreemiumEventCreateReviewStepUpdate';
import useOpenModal from '../../../../../../../hooks/useOpenModal';
import Button from '../../../../../../../components/global/Button';
import EditIcon from '../../../../../../../components/icons/EditIcon';
import CalendarIcon from '../../../../../../../components/icons/CalendarIcon';
import {
  ALBUM_SUBTYPE,
  ALBUM_UPLOAD_PERMISSIONS_LABEL,
} from '../../../../../../../constants/albums';
import { getAsset } from '../../../../../../../util/assets';
import BirthdayEventReviewDetails from './components/BirthdayEventReviewDetails';
import AnniversaryEventReviewDetails from './components/AnniversaryEventReviewDetails';
import GraduationEventReviewDetails from './components/GraduationEventReviewDetails';
import MemorialEventReviewDetails from './components/MemorialEventReviewDetails';
import FundraiserEventReviewDetails from './components/FundraiserEventReviewDetails';
import CustomEventReviewDetails from './components/CustomEventReviewDetails';
import WeddingEventReviewDetails from './components/WeddingEventReviewDetails';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../../../constants/event-items';
import { FREEMIUM_EVENT_STEPS } from '../../constants';
import { formatPriceForDisplay } from '../../../../../../../util/format-money';
import {
  sortEventAddonItemsByPriority,
  getStepArrayFromVariant,
  getEventAddonItemTypeMetaKey,
} from '../../util';
import { FREEMIUM_EVENTS_META } from '../../../../../components/FreemiumEventPurchase/helpers';
import {
  SEARCH_SUBSCRIPTION_PLAN,
  SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD,
} from '../../../../../../temp/views/onboarding/PhotoSearchCheckout/helpers';

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    email_address: PropTypes.string,
  }),
  values: PropTypes.shape({
    name: PropTypes.string,
    date: PropTypes.any,
    albums: PropTypes.arrayOf(PropTypes.string),
    join_code: PropTypes.string,
    join_code_confirm: PropTypes.string,
    hash_code: PropTypes.string,
    privacy: PropTypes.string,
  }).isRequired,
  errors: PropTypes.object,
  steps: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  stepProps: PropTypes.object,
  setStepProps: PropTypes.func.isRequired,
  variant: PropTypes.arrayOf(PropTypes.string),
};

const FreemiumEventCreateReviewStep = ({
  user,
  values,
  steps,
  errors,
  handleChange,
  handleBlur,
  stepProps,
  setStepProps,
  variant,
}) => {
  const openModal = useOpenModal();
  const {
    eventType,
    shouldUseIOSBundles,
    eventAddonItems,
    iOSEventAddonItemsBundle,
    currentSelectedEventAddonItemIds,
    requiresSKUSelection,
    selectedEventAddonItemIds = [],
    searchSubscriptionItem,
    hasActiveSubscription,
    freeEventCreationEnabled,
    isEveryDayShare,
  } = stepProps;

  const filteredSteps = getStepArrayFromVariant(variant, steps);
  const completeEventAddonItems = shouldUseIOSBundles
    ? [iOSEventAddonItemsBundle, ...eventAddonItems]
    : eventAddonItems;

  const selectedEventAddonItems = completeEventAddonItems.filter(
    eventAddonItem =>
      currentSelectedEventAddonItemIds.includes(eventAddonItem.id),
  );

  const availableEventAddonItems = completeEventAddonItems.filter(
    eventAddonItem =>
      !currentSelectedEventAddonItemIds.includes(eventAddonItem.id),
  );

  const shouldIncludePermissionsStep = (() => {
    if (
      stepProps.standaloneEventAddonType === EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK
    ) {
      return true;
    }

    if (
      stepProps.standaloneEventAddonType ===
        EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO &&
      ![ALBUM_SUBTYPE.WEDDINGS, ALBUM_SUBTYPE.MEMORIALS].includes(
        stepProps.eventType,
      )
    ) {
      return true;
    }

    return false;
  })();

  const onRemoveEventAddonItem = eventAddonItem => {
    const eventAddonItemType = eventAddonItem?.types?.[0];

    if (eventAddonItem.types.length > 1) {
      const upgradeBundleIndex = filteredSteps.findIndex(
        filteredStep =>
          filteredStep.key === FREEMIUM_EVENT_STEPS.UPGRADE_BUNDLE,
      );

      stepProps.setActiveStepIndex(upgradeBundleIndex);
      return;
    }

    if (requiresSKUSelection) {
      stepProps.setActiveStepIndex(
        filteredSteps.findIndex(
          filteredStep =>
            filteredStep.key === FREEMIUM_EVENT_STEPS.SKU_SELECTION,
        ),
      );
    } else if (eventAddonItemType === EVENT_ADDON_ITEM_TYPE.WALDOFIED) {
      const subscriptionUpsellIndex = filteredSteps.findIndex(
        step => step.key === FREEMIUM_EVENT_STEPS.SUBSCRIPTION_UPSELL,
      );
      const waldoficationIndex = filteredSteps.findIndex(
        filteredStep => filteredStep.key === FREEMIUM_EVENT_STEPS.WALDOFICATION,
      );
      const upgradeIndex = filteredSteps.findIndex(
        filteredStep => filteredStep.key === FREEMIUM_EVENT_STEPS.UPGRADE,
      );

      if (
        !hasActiveSubscription &&
        !freeEventCreationEnabled &&
        stepProps.subscriptionUpsellCompleted
      ) {
        stepProps.setActiveStepIndex(subscriptionUpsellIndex);
      } else {
        stepProps.setActiveStepIndex(
          waldoficationIndex === -1 ? upgradeIndex : waldoficationIndex,
        );
      }
    } else {
      stepProps.setActiveStepIndex(
        filteredSteps.findIndex(
          filteredStep => filteredStep.key === FREEMIUM_EVENT_STEPS.UPGRADE,
        ),
      );
    } // techincally, add support for MSS/GV if ever required.
  };

  const onRemoveSearchSubscriptionItem = () => {
    stepProps.setActiveStepIndex(
      filteredSteps.findIndex(
        filteredStep =>
          filteredStep.key === FREEMIUM_EVENT_STEPS.SUBSCRIPTION_UPSELL,
      ),
    );
  };

  const renderEventDetails = () => {
    switch (eventType) {
      case ALBUM_SUBTYPE.BIRTHDAYS:
        return (
          <BirthdayEventReviewDetails
            values={values}
            onUpdate={event => {
              event.preventDefault();
              openModal(FREEMIUM_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL, {
                step: steps.DETAILS,
                modalTitle: `Updating ${steps.DETAILS.label}`,
              });
            }}
          />
        );
      case ALBUM_SUBTYPE.ANNIVERSARIES:
        return (
          <AnniversaryEventReviewDetails
            values={values}
            onUpdate={event => {
              event.preventDefault();
              openModal(FREEMIUM_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL, {
                step: steps.DETAILS,
                modalTitle: `Updating ${steps.DETAILS.label}`,
              });
            }}
          />
        );
      case ALBUM_SUBTYPE.GRADUATIONS:
        return (
          <GraduationEventReviewDetails
            values={values}
            onUpdate={event => {
              event.preventDefault();
              openModal(FREEMIUM_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL, {
                step: steps.DETAILS,
                modalTitle: `Updating ${steps.DETAILS.label}`,
              });
            }}
          />
        );
      case ALBUM_SUBTYPE.MEMORIALS:
        return (
          <MemorialEventReviewDetails
            values={values}
            onUpdate={event => {
              event.preventDefault();
              openModal(FREEMIUM_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL, {
                step: steps.DETAILS,
                modalTitle: `Updating ${steps.DETAILS.label}`,
              });
            }}
          />
        );
      case ALBUM_SUBTYPE.FUNDRAISERS:
        return (
          <FundraiserEventReviewDetails
            values={values}
            onUpdate={event => {
              event.preventDefault();
              openModal(FREEMIUM_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL, {
                step: steps.DETAILS,
                modalTitle: `Updating ${steps.DETAILS.label}`,
              });
            }}
          />
        );
      case ALBUM_SUBTYPE.CUSTOM_EVENT:
        return (
          <CustomEventReviewDetails
            values={values}
            onUpdate={event => {
              event.preventDefault();
              openModal(FREEMIUM_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL, {
                step: steps.DETAILS,
                modalTitle: `Updating ${steps.DETAILS.label}`,
              });
            }}
          />
        );
      case ALBUM_SUBTYPE.WEDDINGS:
        return (
          <WeddingEventReviewDetails
            values={values}
            steps={steps}
            onUpdate={(event, step = steps.DETAILS) => {
              event.preventDefault();
              openModal(FREEMIUM_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL, {
                step,
                modalTitle: `Updating ${steps.DETAILS.label}`,
              });
            }}
          />
        );

      // TODO: Add weddings review details.
      default:
        return null;
    }
  };

  const sortedSelectedEventAddons = sortEventAddonItemsByPriority(
    selectedEventAddonItems,
  );

  const sortedAvailableEventAddonItems = sortEventAddonItemsByPriority(
    availableEventAddonItems,
  );

  const showCartSection =
    !!searchSubscriptionItem ||
    sortedSelectedEventAddons?.length > 0 ||
    (!isEveryDayShare && sortedAvailableEventAddonItems?.length > 0);

  return (
    <CreateWizardLayout.Wrapper>
      <CreateWizardLayout.Content size="lg">
        <div className="table-responsive review-table">
          <table className="table table-striped text-xs text-white-space-normal">
            <tbody>
              <tr>
                <td className="text-left">
                  <div className="d-flex justify-content-center align-items-center">
                    <img
                      src={getAsset('/group-photo-illustration.svg')}
                      alt="group"
                      height="60px"
                      className="mr-2 d-none d-sm-block"
                    />
                    <div className="d-flex align-items-flex-start flex-column mr-1">
                      <h2 className="mb-0">{values.name}</h2>
                      <p className="text-base">
                        {eventType === ALBUM_SUBTYPE.MEMORIALS &&
                        values.date_of_birth ? (
                          <>
                            <CalendarIcon color="#9BA8AC" size={12} />{' '}
                            {moment(values.date_of_birth).format('MMMM Do')}
                            {values.date_of_passing
                              ? ` - ${moment(values.date_of_passing).format(
                                  'MMMM Do',
                                )}`
                              : ''}
                          </>
                        ) : (
                          <>
                            {values.date && (
                              <>
                                <CalendarIcon color="#9BA8AC" size={12} />{' '}
                                {moment(values.date).format('MMMM Do')}
                              </>
                            )}
                          </>
                        )}
                      </p>
                    </div>
                    <Button
                      btnStyle="link"
                      btnSize="sm"
                      onClick={event => {
                        event.preventDefault();
                        openModal(
                          FREEMIUM_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL,
                          {
                            step: steps.DETAILS,
                            modalTitle: `Updating ${steps.DETAILS.label}`,
                          },
                        );
                      }}
                      className="review-step-edit-icon ml-auto"
                    >
                      <EditIcon color="#0d84fe" size="18px" className="ml-1" />
                    </Button>
                  </div>
                </td>
              </tr>
              {showCartSection && (
                <tr>
                  <td className="text-left">
                    <div className="d-flex align-items-center">
                      <div className="w-100 d-flex flex-column">
                        <h3 className="mb-0">In cart:</h3>
                        <p className="text-base">
                          {sortedSelectedEventAddons.length === 0 &&
                            !searchSubscriptionItem && (
                              <div className="d-flex align-items-center">
                                {hasActiveSubscription
                                  ? 'Free event album'
                                  : '1 of 5 free events'}
                              </div>
                            )}
                          {sortedSelectedEventAddons.map(eventAddonItem => {
                            // We submit null here as the meta below does not work for VGB.
                            // It instead handles it internally, making it best for us to
                            // send Group Video as the key instead.
                            const eventAddonItemMetaKey = getEventAddonItemTypeMetaKey(
                              eventAddonItem.types,
                              null,
                            );

                            const meta =
                              FREEMIUM_EVENTS_META[eventType].addonItemDetails[
                                eventAddonItemMetaKey
                              ];

                            const removable = !selectedEventAddonItemIds.includes(
                              eventAddonItem.id,
                            );

                            return (
                              <div
                                key={eventAddonItem.id}
                                className="d-flex align-items-center"
                              >
                                <p className="w-100 d-flex align-items-center justify-content-between justify-content-md-start mb-0">
                                  {meta.reviewTitle || meta.title}
                                  {removable && (
                                    <Button
                                      btnStyle="reset"
                                      onClick={() => {
                                        onRemoveEventAddonItem(eventAddonItem);
                                      }}
                                      className="text-extra-bold text-sm ml-auto d-flex"
                                      btnSize="sm"
                                    >
                                      <EditIcon color="#0d84fe" size="18px" />
                                    </Button>
                                  )}
                                </p>
                              </div>
                            );
                          })}

                          {searchSubscriptionItem && (
                            <div
                              key={searchSubscriptionItem.id}
                              className="d-flex align-items-center"
                            >
                              <p className="w-100 d-flex align-items-center justify-content-between justify-content-md-start mb-0">
                                Waldo{' '}
                                {searchSubscriptionItem.plan ===
                                SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PLUS
                                  ? 'Plus'
                                  : 'Premium'}{' '}
                                {searchSubscriptionItem.billing_period ===
                                SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD.MONTHLY
                                  ? 'Monthly'
                                  : 'Annual'}{' '}
                                ($
                                {formatPriceForDisplay(
                                  searchSubscriptionItem.price.value,
                                )}
                                )
                                <Button
                                  btnStyle="reset"
                                  onClick={() => {
                                    onRemoveSearchSubscriptionItem();
                                  }}
                                  className="text-extra-bold text-sm ml-auto d-flex"
                                  btnSize="sm"
                                >
                                  <EditIcon color="#0d84fe" size="18px" />
                                </Button>
                              </p>
                            </div>
                          )}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              {renderEventDetails()}
              {!user?.email_address && values.email_address && (
                <tr>
                  <td className="text-left">
                    <div className="d-flex align-items-center">
                      <div className="d-flex flex-column mr-1">
                        <h3 className="mb-0">Email</h3>
                        <p className="text-base">{values.email_address}</p>
                      </div>
                      <Button
                        btnStyle="link"
                        btnSize="sm"
                        onClick={event => {
                          event.preventDefault();
                          openModal(
                            FREEMIUM_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL,
                            {
                              step: steps.DETAILS,
                              modalTitle: `Updating ${steps.DETAILS.label}`,
                            },
                          );
                        }}
                        className="ml-auto review-step-edit-icon"
                      >
                        <EditIcon
                          color="#0d84fe"
                          size="18px"
                          className="ml-1"
                        />
                      </Button>
                    </div>
                  </td>
                </tr>
              )}
              {shouldIncludePermissionsStep && (
                <tr>
                  <td className="text-left">
                    <div className="d-flex align-items-center">
                      <div className="d-flex flex-column mr-1">
                        <h3 className="mb-0">Permissions</h3>
                        <p className="text-base">
                          {ALBUM_UPLOAD_PERMISSIONS_LABEL[values.permission]}
                          {values.require_review && (
                            <span className="d-block text-primary text-xs">
                              Uploads require review
                            </span>
                          )}
                        </p>
                      </div>
                      <Button
                        btnStyle="link"
                        btnSize="sm"
                        onClick={event => {
                          event.preventDefault();
                          openModal(
                            FREEMIUM_EVENT_CREATE_REVIEW_STEP_UPDATE_MODAL,
                            {
                              step: steps.PERMISSIONS,
                              modalTitle: `Updating ${steps.PERMISSIONS.label}`,
                            },
                          );
                        }}
                        className="ml-auto review-step-edit-icon"
                      >
                        <EditIcon
                          color="#0d84fe"
                          size="18px"
                          className="ml-1"
                        />
                      </Button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <FreemiumEventReviewStepUpdateModal
          errors={errors}
          handleBlur={handleBlur}
          steps={steps}
          values={values}
          handleChange={handleChange}
          stepProps={stepProps}
          setStepProps={setStepProps}
          user={user}
          variant={variant}
        />
      </CreateWizardLayout.Content>
    </CreateWizardLayout.Wrapper>
  );
};

FreemiumEventCreateReviewStep.propTypes = propTypes;

export default compose()(FreemiumEventCreateReviewStep);
